import { useCallback, useEffect, useState } from 'react';
import { Title, useDataProvider, useGetIdentity, useNotify, useTranslate } from 'react-admin';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DevicesIcon from '@mui/icons-material/Devices';
import dayjs from 'dayjs';
import { __t } from '../../i18n/t';

interface DeviceSession {
  id: string;
  deviceName: string | null;
  lastIp: string | null;
  lastUsedAt: string;
  expiresAt: string;
  createdAt: string;
  current: boolean;
}

/**
 * Dispositivos da PRÓPRIA conta, no menu do usuário (topo direito).
 *
 * Só lista e revoga as sessões de quem está logado — o `GET /employees/sessions` filtra
 * pelo `id` do token, então não há como enxergar ou derrubar a sessão de outra pessoa
 * aqui. Para isso existe o relatório "Acessos conectados", exclusivo de admin.
 */
const DeviceSessions = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const { identity } = useGetIdentity();

  const [sessions, setSessions] = useState<DeviceSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await dataProvider.getList('employees/sessions', {
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'lastUsedAt', order: 'DESC' },
        filter: {},
      });
      setSessions((result.data ?? []) as unknown as DeviceSession[]);
    } catch (error) {
      notify('Não foi possível carregar os dispositivos', { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [dataProvider, notify]);

  useEffect(() => { void load(); }, [load]);

  const revoke = async (session: DeviceSession) => {
    setRevoking(session.id);
    try {
      await dataProvider.delete('employees/session', { id: session.id });
      notify('Dispositivo desconectado', { type: 'info' });
      await load();
    } catch (error) {
      notify('Não foi possível desconectar o dispositivo', { type: 'error' });
    } finally {
      setRevoking(null);
    }
  };

  const describeDevice = (session: DeviceSession) => {
    const agent = session.deviceName || '';
    if (/Edg\//.test(agent)) { return 'Edge'; }
    if (/Chrome\//.test(agent)) { return 'Chrome'; }
    if (/Firefox\//.test(agent)) { return 'Firefox'; }
    if (/Safari\//.test(agent)) { return 'Safari'; }
    return translate('Navegador desconhecido', { _: 'Navegador desconhecido' });
  };

  return (
    <Card sx={{ mt: 2 }}>
      <Title title="Meus dispositivos" />
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <DevicesIcon color="primary" />
          <Typography variant="h6">Meus dispositivos</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {identity?.fullName
            ? __t('Estes são os navegadores onde a sua conta (%{nome}) está conectada. Só você vê e encerra estes acessos.', { nome: identity.fullName })
            : __t('Estes são os navegadores onde a sua conta está conectada. Só você vê e encerra estes acessos.')}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Cada dispositivo guarda uma chave própria, que nunca sai daquele navegador.
          Desconectar encerra o acesso dele na próxima renovação — em até 30 minutos.
          Se perdeu o acesso a um computador, desconecte-o aqui e troque sua senha.
        </Typography>

        {loading && sessions.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Dispositivo</TableCell>
                <TableCell>Último IP</TableCell>
                <TableCell>Último acesso</TableCell>
                <TableCell>Conectado desde</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map(session => (
                <TableRow key={session.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {describeDevice(session)}
                      {session.current && (
                        <Chip size="small" color="primary" label="Você está aqui" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{session.lastIp || '—'}</TableCell>
                  <TableCell>{dayjs(session.lastUsedAt).format('DD/MM/YYYY HH:mm')}</TableCell>
                  <TableCell>{dayjs(session.createdAt).format('DD/MM/YYYY HH:mm')}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      color="error"
                      disabled={revoking === session.id}
                      onClick={() => revoke(session)}
                    >
                      {session.current ? __t('Sair deste dispositivo') : __t('Desconectar')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {sessions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum dispositivo conectado.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default DeviceSessions;
